import { useState, useEffect } from "react";
import axios from 'axios';
import API_BASE from './apiBase';
import { CONSENT_TYPES } from './ConsentPage';
import maskUtils from './maskUtils';

const { maskEmail, maskPhone } = maskUtils;

const ENTITY_OPTIONS = ["Employees", "Vendors & Suppliers", "Partners", "Customers"];

const normalizeRecipientValue = (value) => String(value || '').trim().toLowerCase().replace(/[_\s-]+/g, ' ');
const getRecordLanguage = (record) => {
  let data = record?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      data = null;
    }
  }
  const nested = data && typeof data === 'object' ? data : {};
  return record?.preferred_languages
    || record?.preferredLanguages
    || record?.consent_language
    || record?.language_code
    || record?.language
    || nested.consent_language
    || nested.language_code
    || nested.language
    || nested.preferred_languages
    || nested.preferredLanguages
    || '';
};

export const getLatestRecipientLanguage = (records, email) => {
  const emailKey = String(email || '').trim().toLowerCase();
  if (!emailKey || !Array.isArray(records)) return '';

  const matchingRecords = records
    .filter((record) => String(record?.email || '').trim().toLowerCase() === emailKey)
    .sort((a, b) => {
      const aCreated = Date.parse(a?.created_at || '') || 0;
      const bCreated = Date.parse(b?.created_at || '') || 0;
      return bCreated - aCreated || (Number(b?.id) || 0) - (Number(a?.id) || 0);
    });

  return getRecordLanguage(matchingRecords.find((record) => getRecordLanguage(record))) || '';
};

const normalizeEntityValue = (value) => {
  const normalized = normalizeRecipientValue(value);
  const aliases = {
    employees: 'employee',
    emp: 'employee',
    vendors: 'vendor',
    suppliers: 'vendor',
    partners: 'partner',
    'partner(s)': 'partner',
    customers: 'customer',
    clients: 'customer',
  };
  return aliases[normalized] || normalized;
};

const getRecipientMetadata = (recipient) => {
  let data = recipient?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      data = null;
    }
  }

  const nested = data && typeof data === 'object' ? data : {};
  const profile = nested.profile && typeof nested.profile === 'object' ? nested.profile : {};
  const user = nested.user && typeof nested.user === 'object' ? nested.user : {};
  const firstValue = (...values) => values.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ''
  );

  return {
    location: firstValue(
      recipient?.location, recipient?.locality, recipient?.city, recipient?.user_location,
      nested.location, nested.locality, nested.city, nested.user_location,
      profile.location, profile.locality, profile.city,
      user.location, user.locality, user.city
    ),
    department: firstValue(
      recipient?.department, recipient?.department_name, recipient?.dept,
      nested.department, nested.department_name, nested.dept,
      profile.department, profile.department_name,
      user.department, user.department_name
    ),
    entity: firstValue(
      recipient?.entity, recipient?.group, recipient?.group_name, recipient?.user_group, recipient?.entity_type,
      nested.entity, nested.group, nested.group_name, nested.user_group, nested.entity_type,
      profile.entity, profile.group, profile.group_name,
      user.entity, user.group, user.group_name
    ),
  };
};

const getRecipientEntity = (recipient, metadata) => {
  const groupTerms = {
    employee: ['employee', 'staff', 'member'],
    vendor: ['vendor', 'supplier'],
    partner: ['partner', 'affiliate'],
    customer: ['customer', 'client', 'buyer', 'consumer'],
  };
  const matchesGroup = (value, group) => {
    const normalized = normalizeRecipientValue(value);
    const terms = groupTerms[group] || [group];
    return terms.some(term => normalized.includes(term)) ||
      (group === 'employee' && /(^| )emp($| )/.test(normalized));
  };

  const explicitEntity = normalizeEntityValue(metadata.entity);
  for (const group of Object.keys(groupTerms)) {
    if (matchesGroup(explicitEntity, group)) return group;
  }

  const data = (() => {
    if (recipient?.data && typeof recipient.data === 'object') return recipient.data;
    if (typeof recipient?.data === 'string') {
      try {
        const parsed = JSON.parse(recipient.data);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }
    return {};
  })();
  const profile = data.profile && typeof data.profile === 'object' ? data.profile : {};
  const user = data.user && typeof data.user === 'object' ? data.user : {};
  const roleText = [
    recipient?.role, recipient?.user_role, recipient?.type, recipient?.consent_type,
    recipient?.category, recipient?.template, recipient?.consent,
    data.role, data.user_role, data.type, data.consent_type, data.category,
    profile.role, profile.type, profile.consent_type,
    user.role, user.type, user.consent_type,
  ].filter(Boolean).join(' ');

  for (const group of Object.keys(groupTerms)) {
    if (matchesGroup(roleText, group)) return group;
  }
  return '';
};

const DeptIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" fill="currentColor" />
  </svg>
);

const ConsentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM11 15H9v-2h2v2zm4 0h-2v-2h2v2zm-4 4H9v-2h2v2zm4 0h-2v-2h2v2z" fill="currentColor" />
  </svg>
);

// Missing small icons used throughout the component
const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z" fill="currentColor" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s' }}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function SmartConsent({ user }) {
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [entity, setEntity] = useState("");
  const [consentTypes, setConsentTypes] = useState([]);
  const [subConsents, setSubConsents] = useState([]);
  const [loadingSubConsents, setLoadingSubConsents] = useState(false);
  const [showSubConsent, setShowSubConsent] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [consentOptions, setConsentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [fetchedDepartments, setFetchedDepartments] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [recipientRows, setRecipientRows] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientLoadError, setRecipientLoadError] = useState('');
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState(new Set());
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [allConsents, setAllConsents] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [notification, setNotification] = useState(null);
  const [sending, setSending] = useState(false);
  // const selectedDrivePreference = (() => {
  //   try {
  //     return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
  //   } catch (e) {
  //     return '';
  //   }
  // })();
  // const buildFormHeaders = (token) => {
  //   const headers = token ? { Authorization: `Bearer ${token}` } : {};
  //   if (selectedDrivePreference) {
  //     headers['X-Drive-Type'] = selectedDrivePreference;
  //   }
  //   return headers;
  // };
  // map of consent_type -> consent_message (lower-cased key) populated from /manager/new-consents
  const [consentMessages, setConsentMessages] = useState({});

  const recipientKey = (recipient, index = 0) => String(
    recipient?.id || recipient?.public_id || recipient?.user_id || recipient?.email || `recipient-${index}`
  );

  // Departments are tenant-scoped; locations come from the shared config endpoint.
  const departmentOptions = fetchedDepartments && fetchedDepartments.length > 0
    ? fetchedDepartments.map(d => (d.name || d.department || '').toString())
    : [];

  const isStep1Done = Boolean(location);
  const isStep2Done = Boolean(department);
  const isStep3Done = Boolean(entity);
  const isStep4Done = consentTypes.length > 0;
  const selectedRecipientCount = recipientRows.filter((recipient, index) => selectedRecipientKeys.has(recipientKey(recipient, index))).length;
  const canSubmit = isStep1Done && isStep2Done && isStep3Done && isStep4Done && !loadingRecipients && selectedRecipientCount > 0 && (!showSubConsent || (showSubConsent && selectedSubCategory));

  useEffect(() => {
    setDepartment("");
    setEntity("");
    setConsentTypes([]);
  }, [location]);

  useEffect(() => {
    setEntity("");
    setConsentTypes([]);
  }, [department]);

  useEffect(() => {
    setConsentTypes([]);
  }, [entity]);

  // fetch sub-consents when exactly one consent type is selected
  useEffect(() => {
    const fetchForType = async (t) => {
      try {
        const tenantId = getTenantId();
        const params = {};
        if (tenantId) params.tenant_id = tenantId;
        if (t) params.consent_type = t;
        setLoadingSubConsents(true);
        const res = await axios.get(`${API_BASE}/manager/sub-category-consents`, { params });
        const rows = res && res.data && (res.data.rows || res.data) ? (res.data.rows || res.data) : [];
        const mapped = Array.isArray(rows)
          ? rows
            .filter((row) => {
              const parsed = row?.parsed || {};
              return parsed.kind !== 'multilingual_consent'
                && !parsed.is_multilingual_variant
                && row?.parent_sub_consent_id == null;
            })
            .map((row) => ({
              id: row.id,
              label: row.sub_category || row.parsed?.sub_consent_name || row.parsed?.title || row.name || String(row.id),
              message: row.message || row.consent_message || '',
            }))
          : [];
        setSubConsents(mapped);
        const has = mapped && mapped.length > 0;
        setShowSubConsent(has);
        if (!has) setSelectedSubCategory('');
      } catch (e) {
        setSubConsents([]);
        setShowSubConsent(false);
        setSelectedSubCategory('');
      } finally {
        setLoadingSubConsents(false);
      }
    };

    if (consentTypes && consentTypes.length === 1) {
      fetchForType(consentTypes[0]);
    } else {
      // multiple or none selected -> hide sub-consent
      setSubConsents([]);
      setShowSubConsent(false);
      setSelectedSubCategory('');
    }
  }, [consentTypes]);

  // helper to resolve tenant id from runtime user or localStorage
  const getTenantId = () => {
    const userTenantId = Number(user?.tenant_id || user?.tenantId || user?.tenant);
    if (Number.isFinite(userTenantId) && userTenantId > 0) return userTenantId;

    try {
      const w = window || {};
      const globalUser = w.USER || null;
      if (globalUser && (globalUser.tenant_id || globalUser.tenantId || globalUser.tenant)) {
        return Number(globalUser.tenant_id || globalUser.tenantId || globalUser.tenant);
      }
    } catch (e) {
      // ignore
    }
    try {
      const tid = Number(localStorage.getItem('tenant_id'));
      return isNaN(tid) ? null : tid;
    } catch (e) {
      return null;
    }
  };

  // fetch tenant-scoped departments and consent types on mount
  useEffect(() => {
    const tenantId = getTenantId();

    // departments (manager API)
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/manager/consent-options`);
        const configuredLocations = res.data?.location || [];
        const locations = configuredLocations.map(option => {
          if (typeof option === 'string') return option;
          return option.value || option.key || option.label;
        }).filter(Boolean);
        setLocationOptions(locations);
      } catch (e) {
        setLocationOptions([]);
      }
    })();

    (async () => {
      try {
        const url = tenantId ? `${API_BASE}/manager/departments?tenant_id=${encodeURIComponent(tenantId)}` : `${API_BASE}/manager/departments`;
        const res = await axios.get(url);
        const deps = res.data && res.data.departments ? res.data.departments : [];
        setFetchedDepartments(Array.isArray(deps) ? deps : []);
      } catch (e) {
        setFetchedDepartments([]);
      }
    })();

    // Show the latest tenant consent definitions that are active or accepted.
    (async () => {
      try {
        const params = tenantId ? { tenant_id: tenantId } : {};
        const [res, activeTypesRes] = await Promise.all([
          axios.get(`${API_BASE}/manager/new-consents`, { params: { ...params, limit: 500 } }),
          axios.get(`${API_BASE}/user/consent/new_types`, { params }),
        ]);
        const rows = res.data && res.data.data ? res.data.data : [];
        const msgMap = {};
        if (Array.isArray(rows)) {
          for (const r of rows) {
            const t = (r && (r.consent_type || r.type)) ? (r.consent_type || r.type) : null;
            if (t) {
              const key = String(t).trim().toLowerCase();
              const msg = (r && (r.consent_message || r.message || r.consent)) ? (r.consent_message || r.message || r.consent) : '';
              // keep latest non-empty message (rows are ordered by created_at DESC so first wins)
              if (msg && !msgMap[key]) msgMap[key] = msg;
            }
          }
        }
        const types = Array.isArray(activeTypesRes.data?.types) ? activeTypesRes.data.types : [];
        setConsentOptions([...new Set(types.filter((type) => typeof type === 'string' && type.trim()))]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        setConsentMessages(msgMap);
      } catch (e) {
        setConsentOptions([]);
      }
    })();

  }, []);

  // Load only the records for the chosen recipient group, using fast server-side filtering
  useEffect(() => {
    if (!(location && department && entity)) {
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('');
      setLoadingRecipients(false);
      return undefined;
    }

    const tenantId = getTenantId();
    if (!tenantId) {
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('Unable to load recipients because the tenant could not be identified.');
      setLoadingRecipients(false);
      return undefined;
    }

    const controller = new AbortController();
    const loadRecipients = async () => {
      setLoadingRecipients(true);
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('');

      try {
        const smartUrl = `${API_BASE}/user/consent/smart-recipients`;
        const params = {
          tenant_id: tenantId,
          location,
          department,
          entity,
          _recipient_refresh: Date.now(),
        };
        const requestConfig = {
          params,
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          signal: controller.signal,
        };

        let response;
        try {
          response = await axios.get(smartUrl, requestConfig);
        } catch (smartErr) {
          if (controller.signal.aborted) return;
          console.warn('[SmartConsent] smart-recipients endpoint fallback to /user/consent', smartErr);
          const legacyUrl = `${API_BASE}/user/consent`;
          response = await axios.get(legacyUrl, {
            ...requestConfig,
            params: {
              tenant_id: tenantId,
              location,
              department,
              role: entity,
              page: 1,
              page_size: 2000,
              _recipient_refresh: Date.now(),
            }
          });
        }

        if (controller.signal.aborted) return;

        const data = response?.data;
        const recipients = data?.recipients || data?.consents || (Array.isArray(data) ? data : []);
        const allRecords = data?.all_records || (Array.isArray(data?.consents) ? data.consents : recipients);

        const recList = Array.isArray(recipients) ? recipients : [];
        const allList = Array.isArray(allRecords) ? allRecords : recList;

        setAllConsents(allList);

        // If smart-recipients returned deduplicated recipients directly, use them immediately
        if (data?.recipients && Array.isArray(data.recipients)) {
          setRecipientRows(recList);
          setRecipientCount(Number(data.total) || recList.length);
          setSelectedRecipientKeys(new Set(recList.map((recipient, index) => recipientKey(recipient, index))));
        } else {
          // Fallback client filter
          const loc = normalizeRecipientValue(location);
          const dept = normalizeRecipientValue(department);
          const ent = normalizeEntityValue(entity);

          const filtered = allList.filter(r => {
            try {
              const metadata = getRecipientMetadata(r);
              const rl = normalizeRecipientValue(metadata.location || metadata.locality);
              const rd = normalizeRecipientValue(metadata.department);
              const re = getRecipientEntity(r, metadata);
              const matchLocality = rl === loc;
              const matchDept = rd === dept;
              const matchEntity = re === ent || (!re && normalizeEntityValue(metadata.entity) === ent);
              return matchLocality && matchDept && matchEntity;
            } catch (e) {
              return false;
            }
          });
          setRecipientRows(filtered);
          setRecipientCount(filtered.length);
          setSelectedRecipientKeys(new Set(filtered.map((recipient, index) => recipientKey(recipient, index))));
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setAllConsents([]);
          setRecipientRows([]);
          setRecipientCount(0);
          setSelectedRecipientKeys(new Set());
          const detail = error?.response?.data?.detail;
          setRecipientLoadError(
            typeof detail === 'string' && detail.trim()
              ? detail
              : 'Unable to load matching recipients. Please try selecting the group again.'
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoadingRecipients(false);
      }
    };

    loadRecipients();
    return () => controller.abort();
  }, [location, department, entity, user?.tenant_id, user?.tenantId, user?.tenant]);

  const toggleConsent = (type) => {
    setConsentTypes((prev) =>
      prev.includes(type) ? prev.filter((c) => c !== type) : [...prev, type]
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!recipientRows || recipientRows.length === 0) return;
    const selectedRows = recipientRows.filter((recipient, index) => selectedRecipientKeys.has(recipientKey(recipient, index)));
    if (selectedRows.length === 0) return;
    setSending(true);
    const tenantId = getTenantId();
    const token = localStorage.getItem('token') || '';
    // let send = 0;
    let queued = 0;
    let failed = 0;
    let duplicates = 0;
    const payloadsToQueue = [];
    let firstFailure = '';
    const recordFailure = (message) => {
      if (!firstFailure && message) firstFailure = String(message);
    };

    try {
      // send one consent record per recipient per selected consent type
      // Track processed (email,type,sub) tuples to avoid duplicate sends
      const _processed = new Set();
      for (const r of selectedRows) {
        for (const ct of consentTypes) {
          // Build a "best profile" by merging all records for the same email
          // across allConsents — picks the first non-empty value found for
          // each field so partial profiles are reconstructed (DOB, id_proof,
          // preferred_languages, parent details, etc.).
          const emailKey = (r.email || '').toString().trim().toLowerCase();
          const latestLanguage = getLatestRecipientLanguage(allConsents, emailKey);
          let best = { ...r };
          if (emailKey && Array.isArray(allConsents)) {
            const pickFields = [
              'dob', 'phone', 'username', 'full_name', 'name', 'address',
              'id_proof_type', 'id_proof_file', 'preferred_languages',
              'parent_name', 'parent_email', 'parent_phone',
              'location', 'locality', 'department', 'entity',
              'app_name', 'version', 'expiry'
            ];
            for (const ex of allConsents) {
              try {
                const exEmail = (ex.email || '').toString().trim().toLowerCase();
                if (exEmail !== emailKey) continue;
                for (const k of pickFields) {
                  if ((best[k] === undefined || best[k] === null || best[k] === '') &&
                    ex[k] !== undefined && ex[k] !== null && ex[k] !== '') {
                    best[k] = ex[k];
                  }
                }
              } catch (mergeErr) { /* ignore */ }
            }
          }

          // Look up the configured consent message for this type (case-insensitive)
          const ctKey = String(ct || '').trim().toLowerCase();
          const ctMessage = consentMessages[ctKey] || best.consent_message || best.consent || '';
          const recipientLanguage = latestLanguage
            || best.preferred_languages
            || best.preferredLanguages
            || best.consent_language
            || best.language
            || '';
          // Compute DOB and minor flag for parent-fields fallback
          const dobVal = best.dob || best.DOB || best.date_of_birth || null;
          let isMinor = false;
          try {
            if (dobVal) {
              const d = new Date(String(dobVal).slice(0, 10));
              if (!isNaN(d.getTime())) {
                const today = new Date();
                let age = today.getFullYear() - d.getFullYear();
                const m = today.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
                isMinor = age < 18;
              }
            }
          } catch (e) { /* ignore */ }
          const payload = {
            tenant_id: tenantId,
            // user_id: r.user_id || null,
            // username: r.username || r.full_name || r.name || r.Name || '',
            // email: r.email || '',
            // phone: r.phone || '',
            // locality: r.locality || location || null,
            // department: r.department || department || null,
            // entity: r.entity || entity || null,
            user_id: best.user_id || null,
            username: best.username || best.full_name || best.name || best.Name || '',
            email: best.email || '',
            phone: best.phone || '',
            dob: dobVal,
            address: best.address || best.addr || null,
            locality: best.locality || location || null,
            location: best.location || best.locality || location || null,
            department: best.department || department || null,
            entity: best.entity || entity || null,
            type: ct,
            consent_type: ct,
            consent_message: ctMessage,
            consent: ctMessage,
            // pass through identity / compliance fields if present on the source record
            id_proof_type: best.id_proof_type || best.idProofType || null,
            id_proof_file: best.id_proof_file || best.idProofFile || best.id_proof_filename || null,
            preferred_languages: recipientLanguage || null,
            consent_language: recipientLanguage || null,
            // parent details (relevant when minor)
            parent_name: best.parent_name || best.parentName || null,
            parent_email: best.parent_email || best.parentEmail || null,
            parent_phone: best.parent_phone || best.parentPhone || null,
            // metadata
            app_name: best.app_name || best.appName || null,
            version: best.version || best.appVersion || null,
            // expiry: copy from source if present, otherwise leave null (backend default 45)
            expiry: (best.expiry !== undefined && best.expiry !== null && best.expiry !== '') ? best.expiry : null,
            isMinor: isMinor,
            status: 'Pending'
          };
          // dedupe key: normalize email + type + optional sub-category
          try {
            const emailNorm = (payload.email || '').toString().trim().toLowerCase();
            const typeNorm = (payload.type || '').toString().trim().toLowerCase();
            const subNorm = (selectedSubCategory && consentTypes && consentTypes.length === 1 && ct === consentTypes[0]) ? String(selectedSubCategory) : '';
            const key = `${emailNorm}||${typeNorm}||${subNorm}`;
            if (!emailNorm) {
              // no email — skip and count as failed
              failed += 1;
              recordFailure('A selected recipient is missing an email address.');
              continue;
            }
            if (_processed.has(key)) {
              duplicates += 1;
              continue;
            }
            _processed.add(key);
          } catch (ee) { }
          // include selected sub-category when a single consent type is selected and a sub-consent is chosen
          try {
            if (selectedSubCategory && consentTypes && consentTypes.length === 1 && ct === consentTypes[0]) {
              payload.sub_category = selectedSubCategory;
              payload.sub_consent_id = selectedSubCategory;
            }
          } catch (e) { }
          try {
            // skip recipients without an email address
            if (!payload.email || !String(payload.email).trim()) {
              failed += 1;
              recordFailure('A selected recipient is missing an email address.');
              continue;
            }

            // If a matching consent already exists in `allConsents`, skip creating/sending again
            try {
              const emailNorm = (payload.email || '').toString().trim().toLowerCase();
              const typeNorm = (payload.type || '').toString().trim().toLowerCase();
              const subNorm = payload.sub_category ? String(payload.sub_category) : '';
              let exists = false;
              if (Array.isArray(allConsents)) {
                for (const ex of allConsents) {
                  try {
                    const re = (ex.email || '').toString().trim().toLowerCase();
                    const rt = (ex.type || ex.consent_type || '').toString().trim().toLowerCase();
                    const rs = (ex.sub_category || '').toString();
                    if (emailNorm && re === emailNorm && rt === typeNorm && (subNorm === '' || rs === subNorm)) { exists = true; break; }
                  } catch (xx) { /* ignore malformed row */ }
                }
              }
              if (exists) {
                duplicates += 1;
                continue;
              }
            } catch (chkErr) { }
            // const res = await axios.post(`${API_BASE}/user/consent`, payload, { headers : token ? { Authorization: `Bearer ${token}` } : {} });
            payloadsToQueue.push(payload);

            // // Robustly extract id/public_id from response
            // let cid = res?.data?.id ?? res?.data?.ID ?? null;
            // let pub = res?.data?.public_id ?? null;
            // if (!cid && !pub) {
            //   // debug: log create response for investigation
            //   console.debug('create_consent response missing id/public_id', res && res.data);
            //   // fallback: try to find the created consent by querying recent consents for this tenant
            //   try {
            //     const qurl = `${API_BASE}/user/consent?tenant_id=${encodeURIComponent(tenantId)}`;
            //     const qres = await axios.get(qurl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            //     const rows = qres?.data?.consents || [];
            //     // find newest matching by email and type
            //     const emailNorm = (payload.email || '').toString().trim().toLowerCase();
            //     const typeNorm = (payload.type || '').toString().trim().toLowerCase();
            //     let found = null;
            //     for (let i = rows.length - 1; i >= 0; i--) {
            //       const r2 = rows[i];
            //       try {
            //         const re = (r2.email || '').toString().trim().toLowerCase();
            //         const rt = (r2.type || r2.consent_type || '').toString().trim().toLowerCase();
            //         if (emailNorm && re === emailNorm && rt === typeNorm) { found = r2; break; }
            //       } catch (xx) { continue; }
            //     }
            //     if (found) {
            //       if (found.id) cid = found.id;
            //       if (found.public_id) pub = found.public_id;
            //       console.debug('Found created consent via lookup', cid, pub);
            //     }
            //   } catch (lookupErr) {
            //     console.warn('Failed to lookup created consent after create', lookupErr);
            //   }
            // }

            // // attempt to trigger notification: prefer id endpoint, fallback to public_id
            // // Treat created consent as a success even if resend/notification fails, to avoid showing 'failed' when record exists.
            // if (cid) {
            //   try {
            //     const fd = new FormData();
            //     // const headers = token ? { Authorization: `Bearer ${token}` } : {};
            //     const headers = buildFormHeaders(token);
            //     let r1 = await axios.post(`${API_BASE}/user/consent/${cid}/resend`, fd, { headers });
            //     // If backend responded but indicated no send, retry once
            //     if (!(r1 && (r1.status === 200 || r1.status === 201) && (r1.data && (r1.data.sent || r1.data.success)))) {
            //       await new Promise(r => setTimeout(r, 250));
            //       r1 = await axios.post(`${API_BASE}/user/consent/${cid}/resend`, fd, { headers });
            //     }
            //     const sentOk = !!(r1 && r1.data && (r1.data.sent || r1.data.success));
            //     // if (sentOk) sent += 1; else { failed += 1; }
            //     if (sentOk) {
            //       sent += 1;
            //     } else {
            //       // count as created (success) even if notification didn't report sent
            //       sent += 1;
            //     }
            //   } catch (rerr) {
            //     // failed += 1;
            //     // console.warn('Resend failed for consnet id', cid, rerr);
            //     // notification failed but record was created — count as success
            //     sent += 1;
            //     console.warn('Resend failed for consent id (notification); counted as created', cid, rerr);
            //   }
            // } else if (pub) {
            //   try {
            //     const fd = new FormData();
            //     fd.append('public_id', pub);
            //     // const headers = token ? { Authorization: `Bearer ${token}` } : {};
            //     const headers = buildFormHeaders(token);
            //     let r2 = await axios.post(`${API_BASE}/user/consent/resend_by_public`, fd, { headers });
            //     if (!(r2 && (r2.status === 200 || r2.status === 201) && (r2.data && (r2.data.sent || r2.data.success)))) {
            //       await new Promise(r => setTimeout(r, 250));
            //       r2 = await axios.post(`${API_BASE}/user/consent/resend_by_public`, fd, { headers });
            //     }
            //     const sentOk = !!(r2 && r2.data && (r2.data.sent || r2.data.success));
            //     // if (sentOk) sent += 1; else { failed += 1; }
            //     if (sentOk) {
            //       sent += 1;
            //     } else {
            //       sent += 1;
            //     }
            //   } catch (err2) {
            //     // failed += 1;
            //     // console.warn('Failed to trigger resend_by_public', err2);
            //     // notification failed but record was created — count as success
            //     sent += 1;
            //     console.warn('Failed to trigger resend_by_public (notification); counted as created', err2);
            //   }
            // } else {
            //   // No id/public_id returned — log for debugging
            //   console.warn('create_consent: no id or public_id in response', res && res.data);
            //   // failed += 1;
            //   // still treat as created (optimistic) rather than failed
            //   sent += 1;
            // }

          } catch (e) {
            // Existing records are intentionally left alone; new requests are queued by /user/consent.
            try {
              const st = e?.response?.status;
              // const detail = e?.response?.data?.detail || '';
              const responseBody = e?.response?.data;
              const detail = (typeof responseBody === 'string' && responseBody.trim())
                || responseBody?.detail
                || responseBody?.message
                || '';
              if (st === 409 || (typeof detail === 'string' && detail.toLowerCase().includes('duplicate'))) {
                duplicates += 1;
                // attempt to find existing consent and trigger resend
                //   try {
                //     const qurl = `${API_BASE}/user/consent?tenant_id=${encodeURIComponent(tenantId)}`;
                //     const qres = await axios.get(qurl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                //     const rows = qres?.data?.consents || [];
                //     const emailNorm = (payload.email || '').toString().trim().toLowerCase();
                //     const typeNorm = (payload.type || '').toString().trim().toLowerCase();
                //     let found = null;
                //     for (let i = rows.length - 1; i >= 0; i--) {
                //       const r2 = rows[i];
                //       try {
                //         const re = (r2.email || '').toString().trim().toLowerCase();
                //         const rt = (r2.type || r2.consent_type || '').toString().trim().toLowerCase();
                //         if (emailNorm && re === emailNorm && rt === typeNorm) { found = r2; break; }
                //       } catch (xx) { continue; }
                //     }
                //     if (found) {
                //       // trigger resend on existing record
                //       try {
                //         if (found.id) {
                //           const fd = new FormData();
                //           // const headers = token ? { Authorization: `Bearer ${token}` } : {};
                //           const headers = buildFormHeaders(token);
                //           let r1 = await axios.post(`${API_BASE}/user/consent/${found.id}/resend`, fd, { headers });
                //           if (!(r1 && (r1.status === 200 || r1.status === 201) && (r1.data && (r1.data.sent || r1.data.success)))) {
                //             await new Promise(r => setTimeout(r, 250));
                //             r1 = await axios.post(`${API_BASE}/user/consent/${found.id}/resend`, fd, { headers });
                //           }
                //           const sentOk = !!(r1 && r1.data && (r1.data.sent || r1.data.success));
                //           if (sentOk) sent += 1; else { failed += 1; }
                //         } else if (found.public_id) {
                //           const fd = new FormData(); fd.append('public_id', found.public_id);
                //           // const headers = token ? { Authorization: `Bearer ${token}` } : {};
                //           const headers = buildFormHeaders(token);
                //           let r2 = await axios.post(`${API_BASE}/user/consent/resend_by_public`, fd, { headers });
                //           if (!(r2 && (r2.status === 200 || r2.status === 201) && (r2.data && (r2.data.sent || r2.data.success)))) {
                //             await new Promise(r => setTimeout(r, 250));
                //             r2 = await axios.post(`${API_BASE}/user/consent/resend_by_public`, fd, { headers });
                //           }
                //           const sentOk = !!(r2 && r2.data && (r2.data.sent || r2.data.success));
                //           if (sentOk) sent += 1; else { failed += 1; }
                //         } else {
                //           failed += 1;
                //         }
                //       } catch (resendErr) {
                //         failed += 1;
                //         console.warn('Resend for existing consent failed', resendErr);
                //       }
                //     } else {
                //       // no existing found — count as failed
                //       failed += 1;
                //     }
                //   } catch (lookupErr) {
                //     failed += 1;
                //     console.warn('Failed to lookup existing consent after duplicate', lookupErr);
                //   }
                // } else {
                //   failed += 1;
              } else {
                failed += 1;
                recordFailure(detail || `Request failed with status ${st || 'unknown'}.`);
              }
            } catch (xx) {
              failed += 1;
              recordFailure(xx?.message || 'Unexpected error while creating a consent request.');
            }
          }
        }
      }
      if (payloadsToQueue.length > 0) {
        const columns = [
          'user_id', 'username', 'email', 'phone', 'dob', 'address', 'type', 'consent',
          'entity', 'location', 'department', 'id_proof_type', 'id_proof_file',
          'preferred_languages', 'consent_language', 'parent_name', 'parent_email', 'parent_phone',
          'app_name', 'version', 'expiry', 'status'
        ];
        const escapeCsvValue = (value) => {
          const text = value === null || value === undefined ? '' : String(value);
          return `"${text.replace(/"/g, '""')}"`;
        };
        const csv = [
          columns.join(','),
          ...payloadsToQueue.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(','))
        ].join('\r\n');
        const upload = new FormData();
        upload.append('file', new Blob([csv], { type: 'text/csv;charset=utf-8' }), `smart-consent-${Date.now()}.csv`);
        upload.append('tenant_id', String(tenantId));
        if (selectedSubCategory && consentTypes.length === 1) {
          upload.append('sub_consent_id', String(selectedSubCategory));
        }

        const response = await axios.post(`${API_BASE}/user/consent/upload`, upload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response?.data?.accepted) {
          queued = payloadsToQueue.length;
        } else {
          failed += payloadsToQueue.length;
          recordFailure(response?.data?.detail || response?.data?.message || 'Bulk consent job was not accepted by the queue.');
        }
      }
    } catch (e) {
      failed += payloadsToQueue.length;
      const responseBody = e?.response?.data;
      recordFailure(
        (typeof responseBody === 'string' && responseBody.trim())
        || responseBody?.detail
        || responseBody?.message
        || e?.message
        || 'Failed to enqueue the consent batch.'
      );
    }

    setSending(false);
    setSubmitted(true);
    setNotification({ queued, failed, duplicates, location, department, entity, consentTypes: [...consentTypes], firstFailure });
    setConsentTypes([]);
  };

  const reset = () => {
    setLocation("");
    setDepartment("");
    setEntity("");
    setConsentTypes([]);
    setSubmitted(false);
    setNotification(null);
    setActiveDropdown(null);
    setRecipientModalOpen(false);
    setSelectedRecipientKeys(new Set());
  };

  // ── Step badge ──────────────────────────────────────────────────────────────
  const StepBadge = ({ n, done }) => (
    <div style={{
      width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0,
      background: done ? "#2563eb" : "rgba(37,99,235,0.1)",
      border: `1.5px solid ${done ? "#2563eb" : "rgba(37,99,235,0.25)"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: done ? "#fff" : "#2563eb", fontSize: "0.68rem", fontWeight: 700,
      transition: "all 0.3s",
    }}>
      {done ? <CheckIcon /> : n}
    </div>
  );

  // ── Single-select dropdown ──────────────────────────────────────────────────
  const SelectBox = ({ id, label, value, options, onChange, disabled, icon, placeholder }) => {
    const isOpen = activeDropdown === id;
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
      if (!isOpen) setFilterText('');
    }, [isOpen]);

    const visible = (options || []).filter(opt => (opt || '').toString().toLowerCase().includes((filterText || '').toLowerCase()));

    return (
      <div style={{ position: "relative", flex: 1 }}>
        {/* Label */}
        <label style={{
          display: "flex", alignItems: "center", gap: "5px",
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: disabled ? "#888" : "#2563eb",
          marginBottom: "6px", fontFamily: "'DM Sans', sans-serif",
        }}>
          <span style={{ opacity: disabled ? 0.4 : 1 }}>{icon}</span> {label}
        </label>

        {/* Trigger */}
        <div
          // onClick={(e) => !disabled && setActiveDropdown(isOpen ? null : id)}
          onClick={(e) => { e.stopPropagation(); if (!disabled) setActiveDropdown(isOpen ? null : id); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0.7rem 0.85rem",
            background: disabled ? "#f8fafc" : "#ffffff",
            border: `1.5px solid ${value ? "#2563eb" : isOpen ? "#60a5fa" : "#e6eefc"}`,
            borderRadius: "10px", cursor: disabled ? "not-allowed" : "pointer",
            color: disabled ? "#aaa" : value ? "#0f172a" : "#94a3b8",
            fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem",
            boxShadow: isOpen ? "0 0 0 4px rgba(37,99,235,0.08)" : "none",
            transition: "all 0.2s", whiteSpace: "nowrap", overflow: "hidden",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
            {value || placeholder}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, flexShrink: 0 }}>
            <span style={{ color: disabled ? "#bbb" : "#2563eb", marginLeft: "4px", flexShrink: 0 }}>
              <ChevronIcon open={isOpen} />
            </span>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="sc-dropdown" style={{
            position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0,
            background: "#ffffff", border: "1.5px solid rgba(37,99,235,0.15)",
            borderRadius: "10px", zIndex: 200, maxHeight: "240px", overflowY: "auto", overflowX: 'hidden', paddingRight: 8,
            boxShadow: "0 10px 30px rgba(16,24,40,0.08)",
          }}>
            <div style={{ padding: '0.5rem', borderBottom: '1px solid #eef6ff' }} onClick={(e) => e.stopPropagation()}>
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Type to filter..."
                style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e6eefc', fontSize: '0.85rem' }}
              />
            </div>
            <div>
              {visible.map((opt) => (
                <div
                  key={opt}
                  onClick={(e) => { e.stopPropagation(); onChange(opt); setActiveDropdown(null); }}
                  style={{
                    padding: "0.65rem 0.85rem", cursor: "pointer",
                    fontSize: "0.84rem",
                    color: value === opt ? "#2563eb" : "#0f172a",
                    background: value === opt ? "rgba(37,99,235,0.07)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontFamily: "'DM Sans', sans-serif", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = value === opt ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.03)"}
                  onMouseLeave={e => e.currentTarget.style.background = value === opt ? "rgba(37,99,235,0.07)" : "transparent"}
                >
                  {opt}
                  {value === opt && <CheckIcon />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Multi-select dropdown ───────────────────────────────────────────────────
  const MultiSelect = ({ id, options, selected, onToggle, disabled }) => {
    const isOpen = activeDropdown === id;
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
      if (!isOpen) setFilterText('');
    }, [isOpen]);

    const label = selected.length === 0
      ? "Select types…"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

    const visible = (options || []).filter(opt => (opt || '').toString().toLowerCase().includes((filterText || '').toLowerCase()));

    return (
      <div style={{ position: "relative", flex: 1 }}>
        {/* Label */}
        <label style={{
          display: "flex", alignItems: "center", gap: "5px",
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: disabled ? "#888" : "#2563eb",
          marginBottom: "6px", fontFamily: "'DM Sans', sans-serif",
        }}>
          <span style={{ opacity: disabled ? 0.4 : 1 }}><ConsentIcon /></span>
          Consent Types
          {selected.length > 0 && (
            <span style={{
              background: "#2563eb", color: "#fff", borderRadius: "99px",
              padding: "0.05rem 0.45rem", fontSize: "0.62rem",
            }}>
              {selected.length}
            </span>
          )}
        </label>

        {/* Trigger */}
        <div
          onClick={(e) => { e.stopPropagation(); if (!disabled) setActiveDropdown(isOpen ? null : id); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0.7rem 0.85rem",
            background: disabled ? "#f8fafc" : "#ffffff",
            border: `1.5px solid ${selected.length > 0 ? "#2563eb" : isOpen ? "#60a5fa" : "#e6eefc"}`,
            borderRadius: "10px", cursor: disabled ? "not-allowed" : "pointer",
            color: disabled ? "#aaa" : selected.length ? "#0f172a" : "#94a3b8",
            fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem",
            boxShadow: isOpen ? "0 0 0 4px rgba(37,99,235,0.08)" : "none",
            transition: "all 0.2s",
          }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", whiteSpace: 'normal', wordBreak: 'break-word' }}>{label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, flexShrink: 0 }}>
            {selected.length > 0 && (
              <span style={{
                background: "#2563eb", color: "#fff", borderRadius: "99px",
                padding: "0.05rem 0.45rem", fontSize: "0.62rem",
              }}>
                {selected.length}
              </span>
            )}
            <span style={{ color: disabled ? "#bbb" : "#2563eb", marginLeft: "4px", flexShrink: 0 }}>
              <ChevronIcon open={isOpen} />
            </span>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="sc-dropdown" style={{
            position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0,
            background: "#ffffff", border: "1.5px solid rgba(37,99,235,0.15)",
            borderRadius: "10px", zIndex: 200, boxShadow: "0 10px 30px rgba(16,24,40,0.08)",
            maxHeight: "240px", overflowY: "auto", overflowX: 'hidden', paddingRight: 8,
          }}>
            <div style={{ padding: '0.5rem', borderBottom: '1px solid #eef6ff' }} onClick={(e) => e.stopPropagation()}>
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Type to filter..."
                style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e6eefc', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{
              padding: "0.4rem 0.85rem", borderBottom: "1px solid #e6eefc",
              fontSize: "0.66rem", color: "#2563eb", letterSpacing: "0.06em",
              fontFamily: "'DM Sans', sans-serif", textAlign: "center",
            }}>
              Multiple selections allowed
            </div>
            {visible.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <div
                  key={opt}
                  onClick={(e) => { e.stopPropagation(); onToggle(opt); }}
                  style={{
                    padding: "0.65rem 0.85rem", cursor: "pointer",
                    fontSize: "0.84rem",
                    color: checked ? "#2563eb" : "#0f172a",
                    background: checked ? "rgba(37,99,235,0.07)" : "transparent",
                    display: "flex", alignItems: "center", gap: "0.65rem",
                    fontFamily: "'DM Sans', sans-serif", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = checked ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.03)"}
                  onMouseLeave={e => e.currentTarget.style.background = checked ? "rgba(37,99,235,0.07)" : "transparent"}
                >
                  <div style={{
                    width: "16px", height: "16px", borderRadius: "4px", flexShrink: 0,
                    border: `1.5px solid ${checked ? "#2563eb" : "#cbd5e1"}`,
                    background: checked ? "#2563eb" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", transition: "all 0.15s",
                  }}>
                    {checked && <CheckIcon />}
                  </div>
                  <span style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>{opt}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted && notification) {
    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');`}</style>
        <div
          onClick={() => setActiveDropdown(null)}
          style={{
            minHeight: "100vh", background: "#ffffff",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "2rem", fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <div style={{ maxWidth: "560px", width: "100%", textAlign: "center" }}>
            {/* Success icon */}
            <div style={{
              width: "80px", height: "80px", borderRadius: "50%",
              background: "linear-gradient(135deg, #2563eb, #60a5fa)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 1.5rem", boxShadow: "0 0 40px rgba(37,99,235,0.3)",
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2" />
              </svg>
            </div>

            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: "2rem", color: "#0f172a", margin: "0 0 0.5rem" }}>
              {notification.queued > 0 ? 'Consent Requests Queued' : 'Consent Request Failed'}
            </h2>
            <p style={{ color: "#60a5fa", fontSize: "1rem", margin: "0 0 2rem" }}>
              {/* Successfully dispatched to {notification.sent} user */}
              {notification.queued > 0 ? `Queued ${notification.queued} consent requests for email delivery` : 'No consent requests were queued.'}
              {notification.duplicates ? ` · ${notification.duplicates} duplicate(s)` : ''}
              {notification.failed ? ` · ${notification.failed} failed` : ''}
            </p>
            {notification.firstFailure && (
              <p role="status" style={{ color: "#b45309", fontSize: "0.88rem", margin: "-1rem 0 1.5rem" }}>
                First failure: {notification.firstFailure}
              </p>
            )}

            {/* Summary */}
            <div style={{
              background: "rgba(37,99,235,0.05)", border: "1px solid rgba(37,99,235,0.12)",
              borderRadius: "16px", padding: "1.5rem", textAlign: "left", marginBottom: "1.5rem",
            }}>
              {[["Location", notification.location], ["Department", notification.department], ["Group", notification.entity], ["Consents", notification.consentTypes.join(", ")]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: "0.75rem", marginBottom: "0.6rem" }}>
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.08em", minWidth: "90px", paddingTop: "1px" }}>{k}</span>
                  <span style={{ color: "#475569", fontSize: "0.88rem" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Recipients summary */}
            <div style={{
              background: "#f8fafc", border: "1px solid #e6eefc",
              borderRadius: "12px", padding: "1rem", marginBottom: "1.5rem",
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <p style={{ fontSize: "0.72rem", color: "#94a3b8", margin: "0 0 0.25rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    Queued for email
                  </p>
                  <div style={{ fontSize: "0.95rem", color: "#0f172a", fontWeight: 600 }}>
                    {notification.queued} consent requests
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: "0.72rem", color: "#94a3b8", margin: "0 0 0.25rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    Duplicates / Failed
                  </p>
                  <div style={{ fontSize: "0.95rem", color: "#0f172a", fontWeight: 600 }}>
                    {notification.duplicates || 0} duplicate(s) · {notification.failed || 0} failed
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={reset}
              style={{
                background: "linear-gradient(135deg, #2563eb, #60a5fa)",
                color: "#fff", border: "none", padding: "0.9rem 2.5rem",
                borderRadius: "12px", fontSize: "0.95rem", fontWeight: 600,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em",
              }}
            >
              New Consent Request
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .sc-dropdown { scrollbar-width: thin; }
        .sc-dropdown::-webkit-scrollbar { width: 8px; }
        .sc-dropdown::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.3); } 50% { box-shadow: 0 0 0 8px rgba(37,99,235,0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .submit-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 40px rgba(37,99,235,0.28) !important; }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      <div
        onClick={() => setActiveDropdown(null)}
        style={{
          minHeight: "100vh", background: "#ffffff",
          backgroundImage: "radial-gradient(ellipse at 20% 20%, rgba(37,99,235,0.05) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(96,165,250,0.03) 0%, transparent 60%)",
          padding: "2rem 1rem", fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "100%", margin: "0 auto" }}>

          {/* Header */}
          {/* <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "0.5rem",
              background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.12)",
              borderRadius: "99px", padding: "0.3rem 0.9rem", marginBottom: "1rem",
            }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#2563eb", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#60a5fa", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Smart Consent System
              </span>
            </div>
            <h3 style={{
              fontFamily: "'Syne', sans-serif", fontSize: "2.2rem",
              fontWeight: 800, color: "#0f172a", lineHeight: 1.15, marginBottom: "0.5rem",
            }}>
              Smart <span style={{ color: "#2563eb" }}>Consent</span>
            </h3>
            
          </div> */}

          {/* Progress bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "2rem", gap: "0.5rem" }}>
            {[
              { n: 1, label: "Location", done: isStep1Done },
              { n: 2, label: "Department", done: isStep2Done },
              { n: 3, label: "Group", done: isStep3Done },
              { n: 4, label: "Consent", done: isStep4Done },
            ].map((s, i) => (
              <div key={s.n} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <StepBadge n={s.n} done={s.done} />
                  <span style={{
                    fontSize: "0.62rem", color: s.done || i === 0 ? "#2563eb" : "#94a3b8",
                    marginTop: "4px", fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase",
                  }}>
                    {s.label}
                  </span>
                </div>
                {i < 3 && (
                  <div style={{
                    width: "60px", height: "2px", marginBottom: "14px",
                    background: s.done ? "#2563eb" : "#e2e8f0",
                    transition: "background 0.4s", borderRadius: "1px",
                  }} />
                )}
              </div>
            ))}
          </div>

          {/* Form card */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff", border: "1.5px solid #e6eefc",
              borderRadius: "20px", padding: "2rem",
              boxShadow: "0 4px 24px rgba(37,99,235,0.06)",
            }}
          >

            {/* ── Horizontal row of 4 selectors ── */}
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>

              {/* Step 1 – Location */}
              <SelectBox
                id="location"
                label="Location"
                value={location}
                options={locationOptions.slice().sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }))}
                onChange={setLocation}
                icon={<LocationIcon />}
                placeholder="Choose city…"
              />

              {/* Vertical divider */}
              <div style={{ width: "1px", background: "#e6eefc", alignSelf: "stretch", marginTop: "28px" }} />

              {/* Step 2 – Department */}
              <SelectBox
                id="department"
                label="Department"
                value={department}
                options={departmentOptions}
                onChange={setDepartment}
                disabled={!isStep1Done}
                icon={<DeptIcon />}
                placeholder={isStep1Done ? "Choose department…" : "Select location first"}
              />

              {/* Vertical divider */}
              <div style={{ width: "1px", background: "#e6eefc", alignSelf: "stretch", marginTop: "28px" }} />

              {/* Step 3 – Group */}
              <SelectBox
                id="entity"
                label="Group"
                value={entity}
                options={ENTITY_OPTIONS}
                onChange={setEntity}
                disabled={!isStep2Done}
                icon={null}
                placeholder={isStep2Done ? "Choose entity…" : "Select department first"}
              />

              {/* Vertical divider */}
              <div style={{ width: "1px", background: "#e6eefc", alignSelf: "stretch", marginTop: "28px" }} />

              {/* Step 3 – Consent Types */}
              <MultiSelect
                id="consent"
                // options={consentOptions && consentOptions.length ? consentOptions : Object.assign([], CONSENT_TYPES)}
                options={consentOptions && consentOptions.length ? consentOptions : [...CONSENT_TYPES].map(t => typeof t === 'string' ? t : (t.key || t.label || '')).sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }))}
                selected={consentTypes}
                onToggle={toggleConsent}
                disabled={!isStep3Done}
              />

              {/* Sub-consent selector (shown when a single consent type with sub-consents is selected) */}
              {showSubConsent && (
                <div style={{ marginLeft: '1rem', display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Sub Consent <span style={{ color: '#e11d48' }}>*</span></label>
                  {loadingSubConsents ? (
                    <div style={{ padding: 8, color: '#64748b' }}>Loading sub-consents…</div>
                  ) : (
                    <select value={selectedSubCategory} onChange={(e) => setSelectedSubCategory(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }} required>
                      <option value="">-- Select Sub Consent --</option>
                      {(subConsents || []).map(s => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

            </div>

            {/* Selected consent pills */}
            {consentTypes.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "1rem" }}>
                {consentTypes.map((s) => (
                  <span
                    key={s}
                    onClick={() => toggleConsent(s)}
                    style={{
                      background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.3)",
                      color: "#2563eb", borderRadius: "99px", padding: "0.2rem 0.7rem",
                      fontSize: "0.75rem", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: "0.3rem",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {s} <span style={{ opacity: 0.6 }}>×</span>
                  </span>
                ))}
              </div>
            )}

            {/* Recipients preview: show count once location, department & entity selected */}
            {(location && department && entity) && (
              <div style={{
                background: "rgba(37,99,235,0.04)", border: "1px solid rgba(37,99,235,0.1)",
                borderRadius: "12px", padding: "1rem", marginTop: "1.25rem",
              }}>
                <p style={{ fontSize: "0.72rem", color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: "0.65rem" }}>
                  Matching recipients
                </p>
                <p aria-live="polite" style={{ color: "#475569", fontSize: "0.85rem", margin: "0 0 0.5rem" }}>
                  Total matching recipients: {loadingRecipients ? "Loading…" : recipientCount}
                </p>
                <button
                  type="button"
                  onClick={() => setRecipientModalOpen(true)}
                  disabled={loadingRecipients || recipientCount === 0}
                  style={{
                    border: 0, background: "transparent", padding: 0, color: "#0f172a",
                    fontSize: "0.95rem", fontWeight: 600, cursor: recipientCount && !loadingRecipients ? "pointer" : "default",
                    textDecoration: recipientCount ? "underline" : "none", textUnderlineOffset: 3,
                  }}
                >
                  {loadingRecipients ? 'Loading recipients…' : `${recipientCount} users`}
                </button>
                {recipientLoadError && (
                  <p role="status" style={{ color: "#b91c1c", fontSize: "0.82rem", marginTop: "0.5rem" }}>
                    {recipientLoadError}
                  </p>
                )}
              </div>
            )}

            {recipientModalOpen && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Matching recipients"
                onClick={(event) => { if (event.target === event.currentTarget) setRecipientModalOpen(false); }}
                style={{
                  position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
                }}
              >
                <div style={{
                  width: "min(980px, 100%)", maxHeight: "85vh", overflow: "hidden", background: "#fff",
                  borderRadius: 16, boxShadow: "0 24px 70px rgba(15,23,42,0.24)", display: "flex", flexDirection: "column",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "1px solid #e2e8f0" }}>
                    <div>
                      <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 700 }}>Matching recipients</div>
                      <div style={{ color: "#64748b", fontSize: "0.8rem", marginTop: 3 }}>Select the users who should receive this consent.</div>
                    </div>
                    <button type="button" onClick={() => setRecipientModalOpen(false)} aria-label="Close recipients" style={{ border: 0, background: "transparent", color: "#64748b", fontSize: 24, cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ overflow: "auto", padding: "0 1.25rem 1.25rem" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#475569", fontSize: "0.75rem", textAlign: "left" }}>
                          <th style={{ padding: "0.85rem 0.5rem", width: 44 }}>
                            <input
                              type="checkbox"
                              aria-label="Select all recipients"
                              checked={recipientRows.length > 0 && selectedRecipientCount === recipientRows.length}
                              onChange={(event) => setSelectedRecipientKeys(event.target.checked
                                ? new Set(recipientRows.map((recipient, index) => recipientKey(recipient, index)))
                                : new Set())}
                            />
                          </th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>User</th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>Email</th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>Phone</th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>Location</th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>Department</th>
                          <th style={{ padding: "0.85rem 0.5rem" }}>Group</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipientRows.map((recipient, index) => {
                          const key = recipientKey(recipient, index);
                          const name = recipient.username || recipient.full_name || recipient.name || recipient.Name || "Unnamed user";
                          return (
                            <tr key={key} style={{ borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "0.82rem" }}>
                              <td style={{ padding: "0.8rem 0.5rem" }}>
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${name}`}
                                  checked={selectedRecipientKeys.has(key)}
                                  onChange={() => setSelectedRecipientKeys((previous) => {
                                    const next = new Set(previous);
                                    if (next.has(key)) next.delete(key); else next.add(key);
                                    return next;
                                  })}
                                />
                              </td>
                              <td style={{ padding: "0.8rem 0.5rem", fontWeight: 600 }}>{name}</td>
                              <td style={{ padding: "0.8rem 0.5rem" }}>{maskEmail(recipient.email) || "-"}</td>
                              <td style={{ padding: "0.8rem 0.5rem" }}>{maskPhone(recipient.phone) || "-"}</td>
                              <td style={{ padding: "0.8rem 0.5rem" }}>{recipient.location || recipient.locality || location || "-"}</td>
                              <td style={{ padding: "0.8rem 0.5rem" }}>{recipient.department || department || "-"}</td>
                              <td style={{ padding: "0.8rem 0.5rem" }}>{recipient.entity || entity || "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "0.9rem 1.25rem", borderTop: "1px solid #e2e8f0" }}>
                    <span style={{ color: "#475569", fontSize: "0.82rem" }}>{selectedRecipientCount} of {recipientCount} selected</span>
                    <button type="button" onClick={() => setRecipientModalOpen(false)} style={{ border: 0, borderRadius: 8, padding: "0.6rem 1rem", background: "#2563eb", color: "#fff", cursor: "pointer", fontWeight: 600 }}>Done</button>
                  </div>
                </div>
              </div>
            )}

            {/* Submit button */}
            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit || sending}
              style={{
                width: "100%", marginTop: "1.25rem", padding: "1rem",
                background: canSubmit ? "linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)" : "#f1f5f9",
                border: `1px solid ${canSubmit ? "transparent" : "#e2e8f0"}`,
                borderRadius: "12px",
                color: canSubmit ? "#fff" : "#94a3b8",
                fontSize: "0.95rem", fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed",
                fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em",
                transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
              }}
            >
              {sending ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Sending to selected users…
                </>
              ) : canSubmit ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Send Consent to {selectedRecipientCount} Users
                </>
              ) : (
                "Complete all steps to continue"
              )}
            </button>
          </div>


        </div>
      </div>
    </>
  );
}